@if (isRestricted()) {
  <div class="flex flex-column align-items-center justify-content-center p-6 text-center">
    <i class="pi pi-lock text-6xl text-orange-500 mb-4"></i>
    <h3 class="text-2xl font-semibold text-800 mb-2">
      {{ 'workflow.variables.tree.access-restricted.title' | translate }}
    </h3>
    <p class="text-600 mb-0">
      {{ 'workflow.variables.tree.access-restricted.message' | translate }}
    </p>
  </div>
} @else {
  <!-- Tree Table -->
  <p-treeTable
    #tt
    [value]="treeData()"
    [columns]="cols()"
    [loading]="store.isPending()"
    [showLoader]="true"
    [loadingIcon]="'pi pi-spinner pi-spin'"
    scrollHeight="flex"
    [scrollable]="true"
    [rowTrackBy]="trackBy"
    [globalFilterFields]="globalFilterFields()"
    filterMode="strict"
    sortMode="multiple"
    selectionMode="single"
    [selection]="editingStateService.getSelectedNode()"
    (selectionChange)="onSelectionChange($event)"
    (onNodeExpand)="onNodeExpand($event)"
    (onNodeCollapse)="onNodeCollapse($event)"
  >
    <ng-template pTemplate="caption">
      <div class="flex justify-content-between align-items-center">
        <div class="flex gap-1">
          <p-button
            icon="pi pi-plus-circle"
            size="small"
            styleClass="p-button-success"
            [disabled]="!isActionEnabled('create')"
            tooltipPosition="top"
            [pTooltip]="'workflow.variables.tree.actions.add-variable' | translate"
            (onClick)="addVariable()"
          />
          <p-button
            icon="pi pi-folder-plus"
            size="small"
            styleClass="p-button-success opacity-80"
            [disabled]="!isActionEnabled('create')"
            tooltipPosition="top"
            [pTooltip]="'workflow.variables.tree.actions.add-group' | translate"
            (onClick)="addGroup()"
          />
        </div>
        <div class="flex gap-1 align-items-center">
          <div class="flex gap-1">
            <p-button
              icon="pi pi-download"
              size="small"
              [disabled]="!isActionEnabled('export')"
              tooltipPosition="top"
              [pTooltip]="'workflow.variables.tree.actions.export-variables' | translate"
              (onClick)="exportVariables()"
            />
            <p-button
              icon="pi pi-upload"
              size="small"
              [disabled]="!isActionEnabled('import')"
              tooltipPosition="top"
              [pTooltip]="'workflow.variables.tree.actions.import-variables' | translate"
              (onClick)="importVariables()"
            />
          </div>
          <div class="p-input-icon-left">
            <i class="pi pi-search"></i>
            <input
              #searchInput
              type="text"
              pInputText
              class="pl-4 py-2"
              size="small"
              [placeholder]="'workflow.variables.tree.actions.search' | translate"
              [disabled]="!isActionEnabled('search')"
              (input)="tt.filterGlobal(searchInput.value, 'contains')"
            />
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template pTemplate="colgroup" let-columns>
      <colgroup>
        @for (col of columns; track col.field) {
          <col [style.width]="col.width" />
        }
      </colgroup>
    </ng-template>

    <ng-template pTemplate="header" let-columns>
      <tr>
        @for (col of columns; track col.field) {
          <th class="font-semibold text-900 p-3 white-space-nowrap text-overflow-ellipsis overflow-hidden max-w-0">
            @if (col.sortable) {
              <div [ttSortableColumn]="col.field" style="outline: none">
                <span>{{ col.header }}</span>
                <p-treeTableSortIcon [field]="col.field" class="ml-2"></p-treeTableSortIcon>
              </div>
            } @else {
              <span>{{ col.header }}</span>
            }
          </th>
        }
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-rowNode let-rowData="rowData" let-columns="columns">
      @let rowState = getRowState(rowData);
      @if (canEditRow(rowState)) {
        <tr [ttRow]="rowNode">
          <td colspan="8" class="p-0">
            <selfai-platform-variables-edit-form
              [rowData]="rowData"
              [editingMode]="getEditingMode(rowState)"
            ></selfai-platform-variables-edit-form>
          </td>
        </tr>
      } @else {
        <tr [ttRow]="rowNode" [ttSelectableRow]="rowNode" (click)="onRowClick($event, rowNode, rowData)">
          @for (col of columns; track col.field; let i = $index) {
            <td class="p-3 white-space-nowrap text-overflow-ellipsis overflow-hidden">
              @if (i === 0 && (!rowNode.leaf || isGroup(rowData))) {
                <p-treeTableToggler
                  [rowNode]="rowNode"
                  *ngIf="i === 0 && (!rowNode.leaf || isGroup(rowData))"
                  (click)="$event.stopPropagation()"
                />
              }
              @if (i === 0) {
                <i [class]="getNodeIcon(rowData)" class="mr-2 text-primary"></i>
              }

              @switch (col.field) {
                @case ('key') {
                  <span class="text-900 font-semibold">{{ rowData.key || '[New Item]' }}</span>
                }
                @case ('value') {
                  @if (isVariable(rowData)) {
                    <span class="font-mono text-sm text-700 border-round px-2 py-1">
                      {{ rowData.value || 'null' }}
                    </span>
                  }
                }
                @case ('regexRule') {
                  @if (isVariable(rowData)) {
                    <span class="font-mono text-sm text-700 border-round px-2 py-1">
                      {{ rowData.regexRule }}
                    </span>
                  }
                }
                @case ('description') {
                  <span class="text-sm text-700 line-height-3" [title]="rowData.description">
                    {{ rowData.description }}
                  </span>
                }
                @case ('modified') {
                  <span class="text-sm text-600">
                    {{ rowData.modified ? (rowData.modified | date: 'dd.MM.yyyy HH:mm') : '' }}
                  </span>
                }
                @case ('createdAuthor') {
                  <span class="text-sm text-600">{{ rowData.createdAuthor }}</span>
                }
                @case ('used') {
                  @if (isVariable(rowData)) {
                    <selfai-platform-used-in-panel
                      [usedInNodes]="(usedInMap$ | async)?.get(rowData.id)"
                    ></selfai-platform-used-in-panel>
                  }
                }
                @case ('actions') {
                  <div class="flex justify-content-center actions">
                    <p-button
                      icon="pi pi-pencil"
                      size="small"
                      [text]="true"
                      (onClick)="editItem(rowData)"
                      [pTooltip]="'workflow.variables.tree.actions.edit.tooltip' | translate"
                      [disabled]="!isActionEnabled('update') || isNewItem(rowData)"
                    />
                    <p-button
                      icon="pi pi-trash"
                      size="small"
                      severity="danger"
                      [text]="true"
                      (onClick)="deleteItem(rowData)"
                      [pTooltip]="'workflow.variables.tree.actions.delete.tooltip' | translate"
                      [disabled]="!isActionEnabled('delete') || isNewItem(rowData)"
                    />
                    <p-button
                      *ngIf="isVariable(rowData)"
                      icon="pi pi-copy"
                      size="small"
                      [text]="true"
                      (onClick)="copyPasteItem(rowData)"
                      [pTooltip]="'workflow.variables.tree.actions.copy.tooltip' | translate"
                      [disabled]="!isActionEnabled('copy-paste') || isNewItem(rowData)"
                    />
                  </div>
                }
              }
            </td>
          }
        </tr>
      }
    </ng-template>

    <ng-template pTemplate="emptymessage">
      @if (!store.isPending() && store.isInitialized()) {
        <tr>
          <td colspan="8" class="text-center py-4 text-600">
            <i class="pi pi-info-circle mr-2 text-blue-500"></i>
            {{ 'workflow.variables.tree.empty-message' | translate }}
          </td>
        </tr>
      }
    </ng-template>
  </p-treeTable>
}
<p-confirmDialog />
